import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Select } from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import AdminToolsDataService from "../../services/admin-tools.service";
import expressRegisterService from "../../services/expressRegister.service";

import lightThemeBg from "../../assets/images/light_theme_bg.png";
import logoImg from "../../assets/images/logo-img.png";
import footerCompanyLogo1 from "../../assets/images/AGS.png";

import "../../assets/css/login-cinematic.css";
import "../../assets/css/express-register.css";

const { Option } = Select;
const { TextArea } = require("antd").Input;

const QUEUE_KEY = "EXPRESS_REGISTER_QUEUE";
const SUCCESS_AUTOCLOSE_MS = 3000;
const DEFAULT_COUNTRY_ID = 233; // United States — matches Register.js default

const TOTAL_STEPS = 2;
const STEP_NAMES = [
  "Company & Contact Information",
  "Address & Details",
];

const LEAD_TYPES = [
  { value: "Retailer", label: "Retailer" },
  { value: "Diamond Supplier", label: "Diamond Supplier" },
  { value: "Jewelry Wholesaler", label: "Jewelry Wholesaler" },
  { value: "Designer or Manufacturer", label: "Designer or Manufacturer" },
];

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const initialFormState = {
  companyName: "",
  leadType: undefined,
  fullName: "",
  email: "",
  phoneNumber: "",
  referredBy: "",
  streetAddress: "",
  countryId: DEFAULT_COUNTRY_ID,
  stateId: undefined,
  cityId: undefined,
  additionalDetails: "",
};

const initialValidations = {
  companyName: "",
  leadType: "",
  fullName: "",
  email: "",
  phoneNumber: "",
  streetAddress: "",
  countryId: "",
  stateId: "",
  cityId: "",
};

const queueOfflineSubmission = (payload) => {
  try {
    const existing = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
    existing.push({ ...payload, queuedAt: new Date().toISOString() });
    localStorage.setItem(QUEUE_KEY, JSON.stringify(existing));
  } catch (e) {
    console.error("Failed to queue express registration locally:", e);
  }
};

const ExpressRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const WhiteLabeling = useSelector((state) => state.loginReducer);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormState);
  const [validations, setValidations] = useState(initialValidations);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    loadCountries();
    loadStatesForCountry(DEFAULT_COUNTRY_ID);
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // Reset scroll on step change so users see the new step from the top.
  useEffect(() => {
    const formSide = document.querySelector(".express-register-form-side");
    if (formSide) formSide.scrollTop = 0;
  }, [currentStep]);

  const loadCountries = () => {
    AdminToolsDataService.GetLocationDropdownDetails({
      id: 0,
      dropdownType: "Country",
    })
      .then((response) => {
        if (response?.data?.message === "Success" && response.data.responseData) {
          setCountries(response.data.responseData);
        }
      })
      .catch((err) => console.error("loadCountries failed:", err));
  };

  const loadStatesForCountry = (countryId) => {
    if (!countryId) return;
    AdminToolsDataService.GetLocationDropdownDetails({
      id: countryId,
      dropdownType: "State",
    })
      .then((response) => {
        setStates(
          response?.data?.message === "Success"
            ? response.data.responseData || []
            : []
        );
      })
      .catch((err) => console.error("loadStates failed:", err));
  };

  const loadCitiesForState = (stateId) => {
    if (!stateId) return;
    AdminToolsDataService.GetLocationDropdownDetails({
      id: stateId,
      dropdownType: "City",
    })
      .then((response) => {
        setCities(
          response?.data?.message === "Success"
            ? response.data.responseData || []
            : []
        );
      })
      .catch((err) => console.error("loadCities failed:", err));
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validations[name]) {
      setValidations((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCountryChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      countryId: value,
      stateId: undefined,
      cityId: undefined,
    }));
    setStates([]);
    setCities([]);
    setValidations((prev) => ({ ...prev, countryId: "", stateId: "", cityId: "" }));
    loadStatesForCountry(value);
  };

  const handleStateChange = (value) => {
    setFormData((prev) => ({ ...prev, stateId: value, cityId: undefined }));
    setCities([]);
    setValidations((prev) => ({ ...prev, stateId: "", cityId: "" }));
    loadCitiesForState(value);
  };

  const handlePhoneChange = (value, data) => {
    if (data?.dialCode) {
      const withoutCode = value.replace(`+${data.dialCode}`, "");
      handleChange("phoneNumber", withoutCode);
    } else {
      handleChange("phoneNumber", value);
    }
  };

  const validateStep = (step) => {
    const next = { ...validations };
    let ok = true;

    if (step === 0) {
      if (!formData.companyName.trim()) {
        next.companyName = "Company name is required";
        ok = false;
      } else {
        next.companyName = "";
      }
      if (!formData.leadType) {
        next.leadType = "Select a type";
        ok = false;
      } else {
        next.leadType = "";
      }
      if (!formData.fullName.trim()) {
        next.fullName = "Name is required";
        ok = false;
      } else {
        next.fullName = "";
      }
      if (!formData.email.trim()) {
        next.email = "Email is required";
        ok = false;
      } else if (!EMAIL_REGEX.test(formData.email)) {
        next.email = "Email is invalid";
        ok = false;
      } else {
        next.email = "";
      }
      if (!formData.phoneNumber.trim()) {
        next.phoneNumber = "Phone number is required";
        ok = false;
      } else if (formData.phoneNumber.replace(/\D/g, "").length < 7) {
        next.phoneNumber = "Phone number is invalid";
        ok = false;
      } else {
        next.phoneNumber = "";
      }
    } else if (step === 1) {
      if (!formData.streetAddress.trim()) {
        next.streetAddress = "Street address is required";
        ok = false;
      } else {
        next.streetAddress = "";
      }
      if (!formData.countryId) {
        next.countryId = "Select a country";
        ok = false;
      } else {
        next.countryId = "";
      }
      if (!formData.stateId) {
        next.stateId = "Select a state";
        ok = false;
      } else {
        next.stateId = "";
      }
      if (!formData.cityId) {
        next.cityId = "Select a city";
        ok = false;
      } else {
        next.cityId = "";
      }
    }

    setValidations(next);
    return ok;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const buildPayload = () => {
    const country = countries.find((c) => c.id === formData.countryId);
    const state = states.find((s) => s.id === formData.stateId);
    const city = cities.find((c) => c.id === formData.cityId);
    return {
      companyName: formData.companyName.trim(),
      name: formData.fullName.trim(),
      email: formData.email.trim(),
      phone: formData.phoneNumber.trim(),
      address: formData.streetAddress.trim(),
      city: city?.name || "",
      state: state?.name || "",
      country: country?.name || "",
      additionalInformation: formData.additionalDetails.trim(),
      dealerId: 0,
      referBy: formData.referredBy.trim(),
    };
  };

  const showThankYouAndRedirect = () => {
    setSubmitted(true);
    redirectTimerRef.current = setTimeout(() => {
      navigate("/");
    }, SUCCESS_AUTOCLOSE_MS);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;

    setSubmitting(true);
    const payload = buildPayload();
    try {
      const response = await expressRegisterService.InsertJCKShow2026(payload);
      const message = response?.data?.message;
      if (message === "Success" || message === "True") {
        showThankYouAndRedirect();
      } else {
        queueOfflineSubmission(payload);
        showThankYouAndRedirect();
      }
    } catch (err) {
      console.error("Express registration submit failed, queuing locally:", err);
      queueOfflineSubmission(payload);
      showThankYouAndRedirect();
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenLink = (link) => {
    if (link) window.open(link, "__blank");
  };

  const brandName = WhiteLabeling?.whiteLebelData?.[0]?.name || "JewelCloud";
  const brandLogo = WhiteLabeling?.whiteLebelData?.[0]?.logoImage || logoImg;
  const footerLogo = WhiteLabeling?.whiteLebelData?.[0]?.footerLogo || footerCompanyLogo1;
  const footerLogoLink = WhiteLabeling?.whiteLebelData?.[0]?.footerLogoLink || "";

  const renderStepBar = () => (
    <div className="express-register-step-bar">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={i}
          className={`step-pill ${i <= currentStep ? "active" : ""}`}
        />
      ))}
    </div>
  );

  const renderStep0 = () => (
    <>
      <section className="express-register-section">
        <h3 className="express-register-section-header">Company Information</h3>
        <div className="express-register-grid">
          <div className="express-register-field">
            <label>
              Company Name<span className="required-mark">*</span>
            </label>
            <input
              type="text"
              placeholder="Acme Jewelers"
              className={`login-input ${validations.companyName ? "input-error" : ""}`}
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              maxLength={200}
            />
            {validations.companyName && (
              <span className="express-register-field-error">{validations.companyName}</span>
            )}
          </div>

          <div className="express-register-field">
            <label>
              I am a<span className="required-mark">*</span>
            </label>
            <Select
              placeholder="Select type"
              value={formData.leadType || undefined}
              onChange={(value) => handleChange("leadType", value)}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {LEAD_TYPES.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            {validations.leadType && (
              <span className="express-register-field-error">{validations.leadType}</span>
            )}
          </div>
        </div>
      </section>

      <section className="express-register-section">
        <h3 className="express-register-section-header">Contact Information</h3>
        <div className="express-register-grid">
          <div className="express-register-field">
            <label>
              Full Name<span className="required-mark">*</span>
            </label>
            <input
              type="text"
              placeholder="Jane Smith"
              className={`login-input ${validations.fullName ? "input-error" : ""}`}
              value={formData.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              maxLength={120}
            />
            {validations.fullName && (
              <span className="express-register-field-error">{validations.fullName}</span>
            )}
          </div>

          <div className="express-register-field">
            <label>
              Email<span className="required-mark">*</span>
            </label>
            <input
              type="email"
              placeholder="you@company.com"
              className={`login-input ${validations.email ? "input-error" : ""}`}
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              maxLength={200}
            />
            {validations.email && (
              <span className="express-register-field-error">{validations.email}</span>
            )}
          </div>

          <div className="express-register-field">
            <label>
              Phone Number<span className="required-mark">*</span>
            </label>
            <PhoneInput
              country="us"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              inputProps={{ name: "phoneNumber" }}
              enableAreaCodes={true}
            />
            {validations.phoneNumber && (
              <span className="express-register-field-error">{validations.phoneNumber}</span>
            )}
          </div>

          <div className="express-register-field">
            <label>Referred By</label>
            <input
              type="text"
              placeholder="Who referred you? (optional)"
              className="login-input"
              value={formData.referredBy}
              onChange={(e) => handleChange("referredBy", e.target.value)}
              maxLength={200}
            />
          </div>
        </div>
      </section>
    </>
  );

  const renderStep1 = () => (
    <section className="express-register-section">
      <h3 className="express-register-section-header">Address &amp; Details</h3>
      <div className="express-register-grid">
        <div className="express-register-field field-full">
          <label>
            Street Address<span className="required-mark">*</span>
          </label>
          <input
            type="text"
            placeholder="123 Main Street, Suite 400"
            className={`login-input ${validations.streetAddress ? "input-error" : ""}`}
            value={formData.streetAddress}
            onChange={(e) => handleChange("streetAddress", e.target.value)}
            maxLength={300}
          />
          {validations.streetAddress && (
            <span className="express-register-field-error">{validations.streetAddress}</span>
          )}
        </div>

        <div className="express-register-field">
          <label>
            Country<span className="required-mark">*</span>
          </label>
          <Select
            showSearch
            placeholder="Select country"
            value={formData.countryId || undefined}
            onChange={handleCountryChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {countries.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
          {validations.countryId && (
            <span className="express-register-field-error">{validations.countryId}</span>
          )}
        </div>

        <div className="express-register-field">
          <label>
            State<span className="required-mark">*</span>
          </label>
          <Select
            showSearch
            placeholder="Select state"
            value={formData.stateId || undefined}
            onChange={handleStateChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {states.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Select>
          {validations.stateId && (
            <span className="express-register-field-error">{validations.stateId}</span>
          )}
        </div>

        <div className="express-register-field field-full">
          <label>
            City<span className="required-mark">*</span>
          </label>
          <Select
            showSearch
            placeholder="Select city"
            value={formData.cityId || undefined}
            onChange={(value) => handleChange("cityId", value)}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {cities.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>
          {validations.cityId && (
            <span className="express-register-field-error">{validations.cityId}</span>
          )}
        </div>

        <div className="express-register-field field-full">
          <label>Additional Detail</label>
          <TextArea
            rows={3}
            placeholder="Tell us what you're looking for, products of interest, best time to reach out..."
            className="login-input"
            value={formData.additionalDetails}
            onChange={(e) => handleChange("additionalDetails", e.target.value)}
            maxLength={1000}
          />
        </div>
      </div>
    </section>
  );

  const renderCurrentStep = () => {
    if (currentStep === 0) return renderStep0();
    return renderStep1();
  };

  const renderThankYou = () => (
    <div className="express-register-thankyou">
      <div className="express-register-thankyou-icon">
        <i className="fa fa-check" aria-hidden="true" />
      </div>
      <p>Thank you for your registration. Once your application has been reviewed and approved, a representative will contact you to complete the registration process.</p>
      <p className="redirect-hint">Redirecting you back to login...</p>
    </div>
  );

  const renderForm = () => (
    <>
      <h1>Quick Registration</h1>
      <p className="subtitle">
        Drop your details below and our team will follow up. No password required.
      </p>

      <div className="express-register-step-indicator">
        Step <span className="step-current">{currentStep + 1}</span> of {TOTAL_STEPS}
      </div>
      <div className="express-register-step-name">{STEP_NAMES[currentStep]}</div>
      {renderStepBar()}

      {renderCurrentStep()}

      <div className="express-register-actions">
        {currentStep === 0 ? (
          <Button className="btn-secondary" onClick={() => navigate("/")}>
            Back to Login
          </Button>
        ) : (
          <Button className="btn-secondary" onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        {currentStep < TOTAL_STEPS - 1 ? (
          <Button className="btn-primary" type="primary" onClick={handleNext}>
            Next Step
          </Button>
        ) : (
          <Button
            className="btn-primary"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
          >
            Submit Registration
          </Button>
        )}
      </div>

      <p style={{ color: "#64748b", fontSize: 12, marginTop: 20, textAlign: "center" }}>
        Already have a {brandName} account?{" "}
        <Link to="/" style={{ color: "#60a5fa", textDecoration: "none" }}>
          Login here
        </Link>
      </p>
    </>
  );

  return (
    <div className="express-register-page">
      {/* Top header overlay — same pattern as login.js */}
      <div className="login-cinematic-top-header header-view">
        <img
          src={brandLogo}
          alt="Logo"
          className="top-header-logo"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        />
        <div
          className="d-none d-md-block"
          style={{
            color: "#f8fafc",
            fontSize: 13,
            letterSpacing: "2px",
            fontWeight: 500,
            textShadow: "0 2px 4px rgba(0,0,0,0.5)",
          }}
        >
          Jewelry E-commerce Platform | {brandName}
        </div>
      </div>

      <div className="express-register-split">
        <div
          className="express-register-image-side"
          style={{ backgroundImage: `url(${lightThemeBg})` }}
        />
        <div className="express-register-form-side">
          <div className="express-register-card">
            {submitted ? renderThankYou() : renderForm()}
          </div>
        </div>
      </div>

      <footer className="express-register-footer">
        <div className="footer-links">
          <img
            src={footerLogo}
            alt="Logo"
            style={{
              height: 18,
              filter: "brightness(0) invert(1)",
              cursor: "pointer",
            }}
            onClick={() => handleOpenLink(footerLogoLink)}
          />
          <Link to="/termsOfService">{t("Terms of Service")}</Link>
          <Link to="/privacyStatement">{t("Privacy Statement")}</Link>
        </div>
        <div>
          Copyright {new Date().getFullYear()} {brandName}
        </div>
      </footer>
    </div>
  );
};

export default ExpressRegister;
